import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wine, ShieldAlert } from "lucide-react";
import Reveal from "./Reveal";
import { DRINKS, IMAGES } from "@/data/content";

export default function DrinksSection() {
  const [active, setActive] = useState(0);
  const current = DRINKS[active];

  return (
    <section id="bar" data-testid="drinks-section" className="relative bg-[#0D0D0D] py-24 md:py-32 px-6 md:px-10 border-t border-[#C8A24A]/10">
      <div className="mx-auto max-w-[1400px]">
        <Reveal className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-[#C8A24A] uppercase text-xs tracking-[0.35em] mb-4">
            <Wine className="h-4 w-4" /> The Bar
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl">
            Cocktails & <span className="italic text-gold-gradient">Drinks</span>
          </h2>
          <p className="mt-5 text-[#D6D6D6] font-light max-w-xl mx-auto text-sm md:text-base italic">
            Good drinks. Good vibes. Great times.
          </p>
        </Reveal>

        {/* Category tabs */}
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 mb-14 border-y border-[#C8A24A]/15 py-5">
          {DRINKS.map((cat, i) => (
            <button
              key={cat.category}
              onClick={() => setActive(i)}
              data-testid={`drinks-tab-${i}`}
              className={`relative text-xs md:text-sm tracking-[0.12em] uppercase transition-colors ${
                active === i ? "text-[#C8A24A]" : "text-[#D6D6D6]/60 hover:text-[#D6D6D6]"
              }`}
            >
              {cat.category}
              {active === i && (
                <motion.span layoutId="drinks-underline" className="absolute -bottom-1.5 left-0 right-0 h-px bg-[#C8A24A]" />
              )}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1fr_0.85fr] gap-12 lg:gap-20 items-start">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5 }}
              data-testid="drinks-items-list"
            >
              <h3 className="font-display text-3xl md:text-4xl mb-8 text-white">{current.category}</h3>
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-6">
                {current.items.map((item, idx) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -14 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    className="group"
                  >
                    <div className="flex items-end gap-3">
                      <span className="text-[#D6D6D6] group-hover:text-white transition-colors">{item.name}</span>
                      <span className="flex-1 border-b border-dotted border-[#C8A24A]/30 mb-1.5" />
                      <span className="font-display text-lg text-[#C8A24A] shrink-0">
                        {typeof item.price === "number" ? `$${item.price}` : item.price}
                      </span>
                    </div>
                    {item.desc && <p className="text-[#D6D6D6]/50 text-xs font-light mt-0.5">{item.desc}</p>}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="lg:sticky lg:top-28 space-y-6">
            <div className="relative h-72 md:h-[420px] overflow-hidden rounded-sm border border-[#C8A24A]/20 group">
              <img src={IMAGES.cocktail} alt="Signature cocktail" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] to-transparent" />
              <div className="absolute bottom-5 left-5">
                <p className="text-[#C8A24A] text-xs tracking-[0.25em] uppercase">House Pour</p>
                <p className="font-display text-2xl text-white">Handcrafted Signatures</p>
              </div>
            </div>
            <div className="bg-[#181818] border border-[#C8A24A]/15 rounded-sm p-6 text-center">
              <p className="text-[#C8A24A] text-[10px] tracking-[0.3em] uppercase mb-2">Ekaa Reserve</p>
              <p className="font-display text-2xl text-gold-gradient mb-1">Ace of Spades</p>
              <p className="text-[#D6D6D6]/70 font-light text-sm">Our special champagne — $950</p>
            </div>
            <div className="flex items-center justify-center gap-3 text-[#D6D6D6]/60 text-xs tracking-[0.15em] uppercase border border-[#C8A24A]/15 rounded-sm py-4">
              <ShieldAlert className="h-4 w-4 text-[#C8A24A]" /> Please drink responsibly · 19+ only with valid ID
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
